/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Custom fallback UI to show on error */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Page name for error context */
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might send this to an error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI matching app's dark glass theme
      return (
        <div className="min-h-full gradient-mesh p-5 pb-24 flex flex-col items-center justify-center" role="alert" aria-live="assertive">
          <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center animate-slide-up">
            {/* Error Icon */}
            <div className="w-16 h-16 rounded-full bg-ruby/10 flex items-center justify-center mx-auto mb-6 border border-ruby/20" aria-hidden="true">
              <AlertTriangle className="w-8 h-8 text-ruby" />
            </div>

            {/* Error Message */}
            <h1 className="font-display text-2xl font-bold text-text-primary mb-2">
              Something went wrong
            </h1>
            <p className="text-text-muted mb-6">
              {this.props.pageName
                ? `An error occurred on the ${this.props.pageName} page.`
                : 'An unexpected error occurred.'}
            </p>

            {/* Error Details (development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-ruby/5 border border-ruby/20 rounded-xl p-4 mb-6 text-left">
                <p className="text-ruby text-sm font-mono break-all">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-text-muted text-xs cursor-pointer hover:text-text-secondary">
                      Stack trace
                    </summary>
                    <pre className="text-text-muted text-xs mt-2 overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReset}
                className="btn-gold rounded-xl px-4 py-3 flex items-center justify-center gap-2"
                aria-label="Try loading the page again"
              >
                <RefreshCw className="w-5 h-5" aria-hidden="true" />
                <span className="font-semibold">Try Again</span>
              </button>

              <button
                onClick={this.handleGoHome}
                className="btn-ghost rounded-xl px-4 py-3 flex items-center justify-center gap-2"
                aria-label="Navigate to dashboard"
              >
                <Home className="w-5 h-5" aria-hidden="true" />
                <span className="font-medium">Go to Dashboard</span>
              </button>
            </div>

            {/* Support Text */}
            <p className="text-text-muted text-xs mt-6">
              If this keeps happening, try refreshing the page or clearing your browser cache.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-friendly error boundary wrapper for functional components
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  pageName?: string
): React.FC<P> {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary pageName={pageName}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorBoundary;
}

/**
 * Minimal error fallback for less critical sections
 */
export function MinimalErrorFallback({
  message = 'Failed to load this section',
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-4 text-center" role="alert">
      <div className="flex items-center justify-center gap-2 text-ruby mb-2">
        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-medium">Error</span>
      </div>
      <p className="text-text-muted text-sm mb-3">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-gold text-sm font-medium hover:text-gold-light transition-colors"
          aria-label="Retry loading this section"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Lightweight section error boundary for individual UI sections.
 * Shows a minimal inline error rather than replacing the entire page.
 */
interface SectionErrorBoundaryProps {
  children: ReactNode;
  /** Section name for context */
  sectionName?: string;
  /** Whether to show retry button */
  showRetry?: boolean;
  /** Custom fallback component */
  fallback?: ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<SectionErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`SectionErrorBoundary caught error in ${this.props.sectionName || 'section'}:`, error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="glass-card rounded-xl p-4 border border-ruby/20" role="alert">
          <div className="flex items-center gap-2 text-ruby mb-2">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            <span className="text-sm font-medium">
              {this.props.sectionName ? `Failed to load ${this.props.sectionName}` : 'Section error'}
            </span>
          </div>
          {this.props.showRetry !== false && (
            <button
              onClick={this.handleRetry}
              className="text-gold text-xs font-medium hover:text-gold-light transition-colors flex items-center gap-1"
              aria-label={`Retry loading ${this.props.sectionName || 'this section'}`}
            >
              <RefreshCw className="w-3 h-3" aria-hidden="true" />
              Retry
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
